/**
 * Page combinée Détail/Modification d'un Livre Scolaire
 *
 * - Mode lecture par défaut
 * - Bouton Modifier → mode édition inline
 * - Création nouveau livre si id === 'new'
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { ArrowBack, Edit, Save, Cancel } from '@mui/icons-material';
import { SchoolBook, Classe } from '../../types';
import CurriculumApiService from '../../services/curriculumApi';
import SchoolBooksApiService from '../../services/schoolBooksApi';
import { toUserMessage } from '../../lib/http';
import { useNotify } from '../../contexts/NotificationContext';
import { logger } from '../../lib/logger';

const SchoolBookView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNewMode = id === 'new';

  const { environment: env } = useEnvironment();
  const [book, setBook] = useState<SchoolBook | null>(null);
  const [classes, setClasses] = useState<Classe[]>([]);
  const [loading, setLoading] = useState(!isNewMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState(isNewMode);
  const { notify } = useNotify('content');

  // Données du formulaire
  const [formData, setFormData] = useState<Partial<SchoolBook>>({
    title: '',
    description: '',
    nomCourt: '',
    classId: '',
    publisher: 'CEDA',
    academicYear: '2024-2025',
    isActive: false,
    chaptersCount: 0,
    coverImageUrl: ''
  });

  // Charger les classes pour le dropdown
  const loadClasses = useCallback(async () => {
    try {
      const data = await CurriculumApiService.getClasses(env);
      setClasses(data);
    } catch (err) {
      logger.error('Erreur lors du chargement des classes:', err);
    }
  }, [env]);

  // Charger le livre existant
  const loadBook = useCallback(async () => {
    if (isNewMode) return;

    try {
      setLoading(true);
      const data = await SchoolBooksApiService.getSchoolBookById(env, id!);
      setBook(data);
      setFormData(data);
    } catch (err: unknown) {
      setError('Erreur lors du chargement du livre');
    } finally {
      setLoading(false);
    }
  }, [id, env, isNewMode]);

  useEffect(() => {
    loadClasses();
    if (!isNewMode && id) {
      loadBook();
    }
  }, [loadClasses, loadBook, id, isNewMode]);

  // Handlers
  const handleChange = <Field extends keyof SchoolBook>(field: Field, value: SchoolBook[Field]) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    if (isNewMode) {
      navigate('/school-books');
    } else {
      // Restaurer les données originales
      if (book) {
        setFormData(book);
      }
      setIsEditMode(false);
      setError('');
    }
  };

  const validateForm = (): boolean => {
    if (!formData.title || formData.title.trim().length < 3) {
      setError('Le titre doit contenir au moins 3 caractères');
      return false;
    }
    if (!formData.description || formData.description.trim().length < 20) {
      setError('La description doit contenir au moins 20 caractères');
      return false;
    }
    if (!formData.nomCourt || formData.nomCourt.trim().length < 2) {
      setError('Le nom court doit contenir au moins 2 caractères');
      return false;
    }
    if (!formData.academicYear?.match(/^\d{4}-\d{4}$/)) {
      setError('L\'année académique doit être au format YYYY-YYYY (ex: 2024-2025)');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    setError('');

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      if (isNewMode) {
        const response = await SchoolBooksApiService.createSchoolBook(env, formData as Omit<SchoolBook, 'id'>);
        if (response.data?.id) {
          notify('Livre créé avec succès', 'success');
          navigate(`/school-books/${response.data.id}`, { replace: true });
        } else {
          throw new Error(response.error || 'Erreur lors de la création');
        }
      } else {
        await SchoolBooksApiService.updateSchoolBook(env, id!, formData);
        setBook({ ...book, ...formData } as SchoolBook);
        setIsEditMode(false);
        notify('Livre mis à jour avec succès', 'success');
      }
    } catch (err: unknown) {
      setError(toUserMessage(err, 'Erreur lors de la sauvegarde'));
      notify('Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Trouver le nom de la classe
  const getClassName = (classId: string) => {
    const classe = classes.find(c => c.id === classId);
    return classe ? classe.nom : classId;
  };

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Error state (pour un livre existant non trouvé)
  if (!isNewMode && !book && !loading) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Livre non trouvé'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/school-books')} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/school-books')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ flex: 1 }}>
          {isNewMode ? 'Nouveau Livre Scolaire' : (isEditMode ? 'Modifier le livre' : formData.title)}
        </Typography>

        {!isEditMode && !isNewMode && (
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={handleEdit}
          >
            Modifier
          </Button>
        )}

        {isEditMode && (
          <Box>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
              sx={{ mr: 1 }}
              disabled={saving}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {isEditMode ? 'Informations du livre' : 'Informations générales'}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {isEditMode ? (
          /* Mode Édition */
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Titre"
                value={formData.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                required
                helperText="Minimum 3 caractères"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                required
                multiline
                rows={3}
                helperText="Minimum 20 caractères"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom court (titre abrégé)"
                value={formData.nomCourt || ''}
                onChange={(e) => handleChange('nomCourt', e.target.value)}
                required
                helperText="Minimum 2 caractères (ex: Math 3ème, PC Term)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Classe</InputLabel>
                <Select
                  value={formData.classId || ''}
                  label="Classe"
                  onChange={(e) => handleChange('classId', e.target.value)}
                >
                  {classes.map((classe) => (
                    <MenuItem key={classe.id} value={classe.id}>
                      {classe.nom}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Éditeur</InputLabel>
                <Select
                  value={formData.publisher || 'CEDA'}
                  label="Éditeur"
                  onChange={(e) => handleChange('publisher', e.target.value as SchoolBook['publisher'])}
                >
                  <MenuItem value="CEDA">CEDA</MenuItem>
                  <MenuItem value="NEI">NEI</MenuItem>
                  <MenuItem value="EDUCI">EDUCI</MenuItem>
                  <MenuItem value="Autre">Autre</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Année académique"
                value={formData.academicYear || ''}
                onChange={(e) => handleChange('academicYear', e.target.value)}
                required
                placeholder="2024-2025"
                helperText="Format: YYYY-YYYY"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre de chapitres"
                value={formData.chaptersCount || 0}
                type="number"
                InputProps={{ readOnly: true }}
                helperText="Calculé automatiquement"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL de l'image de couverture (optionnel)"
                value={formData.coverImageUrl || ''}
                onChange={(e) => handleChange('coverImageUrl', e.target.value)}
                placeholder="https://..."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive || false}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                  />
                }
                label="Livre actif et visible aux élèves"
              />
            </Grid>
          </Grid>
        ) : (
          /* Mode Lecture */
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Description
              </Typography>
              <Typography variant="body1" paragraph>
                {formData.description || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Nom court
              </Typography>
              <Typography variant="body1">
                {formData.nomCourt || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Classe
              </Typography>
              <Chip label={getClassName(formData.classId || '')} color="primary" size="small" />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Éditeur
              </Typography>
              <Typography variant="body1">{formData.publisher || '-'}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Année académique
              </Typography>
              <Typography variant="body1">{formData.academicYear || '-'}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Nombre de chapitres
              </Typography>
              <Typography variant="body1">{formData.chaptersCount || 0}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Statut
              </Typography>
              <Chip
                label={formData.isActive ? 'Actif' : 'Inactif'}
                color={formData.isActive ? 'success' : 'default'}
                size="small"
              />
            </Grid>

            {formData.coverImageUrl && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Image de couverture
                </Typography>
                <img
                  src={formData.coverImageUrl}
                  alt={formData.title}
                  style={{ maxWidth: '300px', maxHeight: '400px', objectFit: 'contain' }}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Paper>

    </Box>
  );
};

export default SchoolBookView;
